import { getWhatsAppConfig } from '../config/environment';

/** Must match backend / admin save key */
export const CONTACT_CHANNELS_STORAGE_KEY = 'sheenwear_contact_channels';

/** Fired after admin saves WhatsApp numbers (same tab + detail). */
export const CONTACT_CHANNELS_UPDATED_EVENT = 'sheenwearContactChannelsUpdated';

export const DEFAULT_FREE_DELIVERY_MIN_AMOUNT = 5000;

/** Charged when the cart does not qualify for free delivery (or free delivery is off). */
export const DEFAULT_STANDARD_SHIPPING_FEE_PKR = 200;

/** Optional profile URLs for footer / contact page (https only after normalize). */
export function normalizeSocialUrl(raw) {
  let s = String(raw ?? '').trim();
  if (!s) return '';
  const lower = s.toLowerCase();
  const jsScheme = ['java', 'script'].join('') + ':';
  if (lower.startsWith(jsScheme) || lower.startsWith('data:')) return '';
  if (s.startsWith('//')) s = `https:${s}`;
  if (!/^https?:\/\//i.test(s)) s = `https://${s}`;
  try {
    const u = new URL(s);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return '';
    return u.href;
  } catch {
    return '';
  }
}

/** Full shape stored under CONTACT_CHANNELS_STORAGE_KEY + API /settings/contact */
export function normalizeContactChannelsData(input) {
  const o = input && typeof input === 'object' ? input : {};
  let min = Number(o.freeDeliveryMinAmount);
  if (!Number.isFinite(min) || min < 0) {
    min = DEFAULT_FREE_DELIVERY_MIN_AMOUNT;
  }
  min = Math.round(min);
  let stdFee = Number(o.standardShippingFee);
  if (!Number.isFinite(stdFee) || stdFee < 0) {
    stdFee = DEFAULT_STANDARD_SHIPPING_FEE_PKR;
  }
  stdFee = Math.round(stdFee);
  const rawEn = o.freeDeliveryEnabled;
  const freeDeliveryEnabled =
    rawEn === undefined || rawEn === null ? true : Boolean(rawEn);
  return {
    whatsappChatNumber: String(o.whatsappChatNumber ?? '').trim(),
    whatsappOrderNumber: String(o.whatsappOrderNumber ?? '').trim(),
    twitterUrl: normalizeSocialUrl(o.twitterUrl),
    facebookUrl: normalizeSocialUrl(o.facebookUrl),
    instagramUrl: normalizeSocialUrl(o.instagramUrl),
    tiktokUrl: normalizeSocialUrl(o.tiktokUrl),
    freeDeliveryEnabled,
    freeDeliveryMinAmount: min,
    standardShippingFee: stdFee,
  };
}

/**
 * @param {number} subtotal — after sale/coupon, before shipping (same basis as existing checkout logic)
 * @param {object} rawContact — waContact / API data (may be partial)
 */
export function computeShippingFromSubtotal(subtotal, rawContact) {
  const n = normalizeContactChannelsData(rawContact || {});
  const fee = n.standardShippingFee;
  const sub = Number(subtotal) || 0;
  if (!n.freeDeliveryEnabled) {
    return {
      shipping: fee,
      standardShippingFee: fee,
      freeDeliveryEnabled: false,
      threshold: n.freeDeliveryMinAmount,
      qualified: false,
      amountToAdd: null,
    };
  }
  const qualified = sub >= n.freeDeliveryMinAmount;
  const amountToAdd = qualified ? 0 : Math.max(0, n.freeDeliveryMinAmount - sub);
  return {
    shipping: qualified ? 0 : fee,
    standardShippingFee: fee,
    freeDeliveryEnabled: true,
    threshold: n.freeDeliveryMinAmount,
    qualified,
    amountToAdd,
  };
}

/** Digits only for wa.me (country code included, no +) */
export function normalizeWhatsappDigits(input) {
  return String(input || '').replace(/\D/g, '');
}

export function buildWhatsAppUrl(phone, message) {
  const d = normalizeWhatsappDigits(phone);
  if (!d) return '';
  const q = message ? `?text=${encodeURIComponent(message)}` : '';
  return `https://wa.me/${d}${q}`;
}

export function parseContactChannelsFromStorage() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(CONTACT_CHANNELS_STORAGE_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw);
    return o && typeof o === 'object' ? o : null;
  } catch {
    return null;
  }
}

/**
 * "Order via WhatsApp" number (product page, etc.):
 * admin `whatsappOrderNumber` → else `whatsappChatNumber` → else env `REACT_APP_WHATSAPP_BUSINESS_NUMBER`.
 */
export function getWhatsAppOrderPhoneDigits() {
  const ch = parseContactChannelsFromStorage();
  const order = normalizeWhatsappDigits(ch?.whatsappOrderNumber);
  if (order) return order;
  const chat = normalizeWhatsappDigits(ch?.whatsappChatNumber);
  if (chat) return chat;
  return normalizeWhatsappDigits(getWhatsAppConfig().businessNumber || '');
}

/**
 * Digits for the same WhatsApp line as {@link getWhatsAppChatHelpUrl} (chat → order → fallback).
 * Use with {@link buildWhatsAppUrl} when you need a custom prefilled message.
 */
export function getWhatsAppChatHelpPhoneDigits() {
  const n = normalizeContactChannelsData(parseContactChannelsFromStorage() || {});
  const chatPhone = n.whatsappChatNumber || n.whatsappOrderNumber || '';
  let d = normalizeWhatsappDigits(chatPhone);
  if (!d) d = getWhatsAppOrderPhoneDigits();
  return d;
}

/** Storefront “chat with us” link: admin chat → order → env (same basis as bag/checkout). */
export function getWhatsAppChatHelpUrl() {
  const n = normalizeContactChannelsData(parseContactChannelsFromStorage() || {});
  const chatPhone = n.whatsappChatNumber || n.whatsappOrderNumber || '';
  const msg = 'Hi! I need help with my order or promo codes.';
  let url = buildWhatsAppUrl(chatPhone, msg);
  if (!url) {
    url = buildWhatsAppUrl(getWhatsAppOrderPhoneDigits(), msg);
  }
  return url;
}

export function persistContactChannelsLocal(partial) {
  if (typeof window === 'undefined') return;
  try {
    const prev = parseContactChannelsFromStorage() || {};
    const merged = normalizeContactChannelsData({ ...prev, ...partial });
    localStorage.setItem(CONTACT_CHANNELS_STORAGE_KEY, JSON.stringify(merged));
    window.dispatchEvent(new CustomEvent(CONTACT_CHANNELS_UPDATED_EVENT, { detail: merged }));
  } catch (e) {
    console.warn('persistContactChannelsLocal', e);
  }
}
